import React, { useState, useEffect } from "react";
import "./CrudView.css";

function CrudView() {
    const [venues, setVenues] = useState([]);
    const [selectedVenues, setSelectedVenues] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false)
    const [uploadError, setUploadError] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [saveError, setSaveError] = useState(null);    const [uploadingImage, setUploadingImage] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedVenueId, setSelectedVenueId] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [editingVenueId, setEditingVenueId] = useState(null);
    const [editedVenues, setEditedVenues] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [venueToDelete, setVenueToDelete] = useState(null);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [venueToSave, setVenueToSave] = useState(null);

    // Amenities list for the checkboxes
    const amenitiesList = [
        { id: 'has_parking', label: 'Parking' },
        { id: 'has_cafe', label: 'Cafe' },
        { id: 'has_seating', label: 'Seating' },
        { id: 'has_vending', label: 'Vending' },
        { id: 'has_disability_access', label: 'Disability Access' },
        { id: 'has_changing_rooms', label: 'Changing Rooms' },
        { id: 'has_showers', label: 'Showers' },
        { id: 'has_toilets', label: 'Toilets' },
        { id: 'has_gym', label: 'Gym' },
        { id: 'has_fitness_studio', label: 'Fitness Studio' },
        { id: 'has_community_room', label: 'Community Room' }
    ];

    // Fetch unverified venues from backend
    useEffect(() => {
        const fetchUnverifiedVenues = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/admin/venues`);

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error('Failed to fetch venues: ', errorText);
                }

                const responseData = await response.text();
                const data = JSON.parse(responseData);
                setVenues(data);
            } catch (err) {
                setLoadError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUnverifiedVenues();
    }, []);

    const updateVenue = async (venueId, data) => {
        const response = await fetch(`${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/admin/venues/${venueId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });
      
        if (!response.ok) {
          throw new Error('Failed to update venue');
        }
      
        return await response.json(); // assuming backend returns the updated venue
      };
      
      const deleteVenue = async (venueId) => {
        const response = await fetch(`${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/admin/venues/${venueId}`, {
          method: 'DELETE',
        });
      
        if (!response.ok) {
          throw new Error('Failed to delete venue');
        }
      
        return await response.json(); // optional, depending on your backend
      };
      

    // Handle checkbox selection
    const handleCheckboxChange = (venueId) => {
        setSelectedVenues(prev => {
            const newSelection = prev.includes(venueId)
                ? prev.filter(id => id !== venueId)
                : [...prev, venueId];
            return newSelection;
        });
    };

    // Handle edit button click
    const handleEdit = (venueId) => {
        setEditingVenueId(venueId);
        // Initialize edited values with current venue data
        const venueToEdit = venues.find(venue => venue.id === venueId);
        setEditedVenues(prev => ({
            ...prev,
            [venueId]: { ...venueToEdit }
        }));
    };

    // Handle field changes during editing
    const handleFieldChange = (venueId, field, value) => {
        setEditedVenues(prev => ({
            ...prev,
            [venueId]: {
                ...prev[venueId],
                [field]: value
            }
        }));
    };

    // Handle amenities checkbox changes
    const handleAmenityChange = (venueId, amenityId, isChecked) => {
        setEditedVenues(prev => ({
            ...prev,
            [venueId]: {
                ...prev[venueId],
                [amenityId]: isChecked
            }
        }));
    };

    // Handle save button click
    const handleSaveClick = (venueId) => {
        setVenueToSave(venueId);
        setShowSaveConfirm(true);
    };

    // Confirm save changes
    const confirmSave = async () => {
        if (!venueToSave) return;
      
        try {
          setIsLoading(true);
          setSaveError(null);
      
          const venueData = editedVenues[venueToSave];
      
          const updatedVenue = await updateVenue(venueToSave, venueData);
      
          // Update local state with response
          setVenues(prev =>
            prev.map(venue =>
              venue.id === updatedVenue.id ? { ...venue, ...updatedVenue } : venue
            )
          );
      
          setEditingVenueId(null);
          setEditedVenues(prev => {
            const newEdited = { ...prev };
            delete newEdited[venueToSave];
            return newEdited;
          });
      
          setShowSaveConfirm(false);
          setVenueToSave(null);
          setSaveError(false)
      
        } catch (err) {
          setSaveError(err.message);
        } finally {
          setIsLoading(false);
        }
      };
      

    // Handle delete button click
    const handleDeleteClick = (venueId) => {
        setVenueToDelete(venueId);
        setShowDeleteConfirm(true);
    };

    // Confirm delete
    const confirmDelete = async () => {
        if (!venueToDelete) return;
      
        try {
          setIsLoading(true);
          setDeleteError(null);
      
          await deleteVenue(venueToDelete);
      
          setVenues(prev => prev.filter(v => v.id !== venueToDelete));
          setShowDeleteConfirm(false);
          setVenueToDelete(null);
      
        } catch (err) {
          setDeleteError(err.message);
        } finally {
          setIsLoading(false);
        }
      };
      

    // Handle image upload modal open
    const handleUploadClick = (venueId) => {
        setSelectedVenueId(venueId);
        setShowUploadModal(true);
    };

    // Handle file selection
    const handleFileChange = (e) => {
        setImageFile(e.target.files[0]);
    };

    // Handle image upload
    const handleImageUpload = async () => {
        if (!imageFile || !selectedVenueId) return;

        try {
            setUploadingImage(selectedVenueId);

            const formData = new FormData();
            formData.append('images', imageFile);
            formData.append('venueId', selectedVenueId);

            const response = await fetch(`${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/admin/upload-venue-image`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Image upload failed');
            }

            const updatedVenue = await response.json();

            // Update the venue in state
            setVenues(prev =>
                prev.map(venue =>
                    venue.id === updatedVenue.id ? updatedVenue : venue
                )
            );

            // Reset upload state
            setShowUploadModal(false);
            setImageFile(null);
            setSelectedVenueId(null);
        } catch (err) {
            setUploadError(err.message);
        } finally {
            setUploadingImage(null);
        }
    };

    // Format amenities for display
    const formatAmenities = (venue) => {
        return amenitiesList
            .filter(amenity => venue[amenity.id])
            .map(amenity => amenity.label)
            .join(", ") || 'None';
    };

    if (isLoading) {
        return <div className="loading">Loading venues...</div>;
    }

    if (loadError) {
        return <div className="error">Error: {loadError}</div>;
    }

    return (
        <div className="overview_page">
            {/* Upload Image Modal */}
            {showUploadModal && (
                <div className="modal-overlay">
                    <div className="upload-modal">
                        <h3>Upload Image for Venue</h3>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            disabled={uploadingImage}
                        />
                        {uploadError && <p className="error-text">{uploadError}</p>}
                        <div className="modal-actions">
                            <button
                                onClick={handleImageUpload}
                                disabled={!imageFile || uploadingImage}
                                className="upload-button"
                            >
                                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setUploadError(false);
                                    setImageFile(null);
                                }}
                                className="cancel-button"
                            >
                                Cancel
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="confirmation-modal">
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete {venues.find(v => v.id === venueToDelete)?.name || "this venue"}?</p>
                        {deleteError && <p className="error-text">{deleteError}</p>}
                        <div className="modal-actions">
                            <button
                                onClick={confirmDelete}
                                className="delete-button"
                            >
                                Delete
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false) && setDeleteError(false)}
                                className="cancel-button"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Save Confirmation Modal */}
            {showSaveConfirm && (
                <div className="modal-overlay">
                    <div className="confirmation-modal">
                        <h3>Confirm Save</h3>
                        <p>Are you sure you want to save your changes to {venues.find(v => v.id === venueToSave)?.name || "this venue"}?</p>
                        {saveError && <p className="error-text">{saveError}</p>}
                        <div className="modal-actions">
                            <button
                                onClick={confirmSave}
                                className="save-button"
                            >
                                Save
                            </button>
                            <button
                                onClick={() => setShowSaveConfirm(false) && setSaveError(false)}
                                className="cancel-button"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="approval-header">
                <h2>Venues</h2>
            </div>

            <div className="table-container">
                <table className="venues-table">
                    <thead>
                        <tr>
                            <th style={{ width: '50px' }}>Select</th>
                            <th>ID</th>
                            <th>Venue Name</th>
                            <th>Description</th>
                            <th>Capacity</th>
                            <th>Verified</th>
                            <th>Location</th>
                            <th>Latitude</th>
                            <th>Longitude</th>
                            <th>Contact</th>
                            <th>Amenities</th>
                            <th>Courts</th>
                            <th>Images</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {venues.length > 0 ? (
                            venues.map(venue => (
                                <tr
                                    key={venue.id}
                                    className={selectedVenues.includes(venue.id) ? 'selected-row' : ''}
                                >
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedVenues.includes(venue.id)}
                                            onChange={() => handleCheckboxChange(venue.id)}
                                        />
                                    </td>
                                    <td>{venue.id}</td>
                                    <td>
                                        {editingVenueId === venue.id ? (
                                            <input
                                                type="text"
                                                value={editedVenues[venue.id]?.name || ''}
                                                onChange={(e) => handleFieldChange(venue.id, 'name', e.target.value)}
                                            />
                                        ) : (
                                            venue.name
                                        )}
                                    </td>
                                    <td className="description-cell">
                                        {editingVenueId === venue.id ? (
                                            <textarea
                                                value={editedVenues[venue.id]?.description || ''}
                                                onChange={(e) => handleFieldChange(venue.id, 'description', e.target.value)}
                                            />
                                        ) : (
                                            venue.description || 'N/A'
                                        )}
                                    </td>
                                    <td>
                                        {editingVenueId === venue.id ? (
                                            <input
                                                type="number"
                                                value={editedVenues[venue.id]?.capacity || ''}
                                                onChange={(e) => handleFieldChange(venue.id, 'capacity', e.target.value)}
                                            />
                                        ) : (
                                            venue.capacity || 'N/A'
                                        )}
                                    </td>
                                    <td>
                                        {editingVenueId === venue.id ? (
                                            <input
                                                type="checkbox"
                                                checked={editedVenues[venue.id]?.is_verified || false}
                                                onChange={(e) => handleFieldChange(venue.id, 'is_verified', e.target.checked)}
                                            />
                                        ) : (
                                            venue.is_verified ? 'Yes' : 'No'
                                        )}
                                    </td>
                                    <td>
                                        {editingVenueId === venue.id ? (
                                            <>
                                                <input
                                                    type="text"
                                                    value={editedVenues[venue.id]?.address_line1 || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'address_line1', e.target.value)}
                                                    placeholder="Address Line 1"
                                                /><br />
                                                <input
                                                    type="text"
                                                    value={editedVenues[venue.id]?.address_line2 || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'address_line2', e.target.value)}
                                                    placeholder="Address Line 2"
                                                /><br />
                                                <input
                                                    type="text"
                                                    value={editedVenues[venue.id]?.city || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'city', e.target.value)}
                                                    placeholder="City"
                                                />,{' '}
                                                <input
                                                    type="text"
                                                    value={editedVenues[venue.id]?.state || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'state', e.target.value)}
                                                    placeholder="State"
                                                />{' '}
                                                <input
                                                    type="text"
                                                    value={editedVenues[venue.id]?.postcode || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'postcode', e.target.value)}
                                                    placeholder="Postcode"
                                                /><br />
                                                <input
                                                    type="text"
                                                    value={editedVenues[venue.id]?.country || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'country', e.target.value)}
                                                    placeholder="Country"
                                                />
                                            </>
                                        ) : (
                                            <>
                                                {venue.address_line1}<br />
                                                {venue.address_line2 && <>{venue.address_line2}<br /></>}
                                                {venue.city}, {venue.state} {venue.postcode}<br />
                                                {venue.country}
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        {editingVenueId === venue.id ? (
                                            <input
                                                type="number"
                                                step="0.000001"
                                                value={editedVenues[venue.id]?.latitude || ''}
                                                onChange={(e) => handleFieldChange(venue.id, 'latitude', e.target.value)}
                                            />
                                        ) : (
                                            venue.latitude || 'N/A'
                                        )}
                                    </td>
                                    <td>
                                        {editingVenueId === venue.id ? (
                                            <input
                                                type="number"
                                                step="0.000001"
                                                value={editedVenues[venue.id]?.longitude || ''}
                                                onChange={(e) => handleFieldChange(venue.id, 'longitude', e.target.value)}
                                            />
                                        ) : (
                                            venue.longitude || 'N/A'
                                        )}
                                    </td>
                                    <td>
                                        {editingVenueId === venue.id ? (
                                            <>
                                                <input
                                                    type="email"
                                                    value={editedVenues[venue.id]?.contact_email || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'contact_email', e.target.value)}
                                                    placeholder="Email"
                                                /><br />
                                                <input
                                                    type="tel"
                                                    value={editedVenues[venue.id]?.contact_phone || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'contact_phone', e.target.value)}
                                                    placeholder="Phone"
                                                /><br />
                                                <input
                                                    type="url"
                                                    value={editedVenues[venue.id]?.contact_website || ''}
                                                    onChange={(e) => handleFieldChange(venue.id, 'contact_website', e.target.value)}
                                                    placeholder="Website"
                                                />
                                            </>
                                        ) : (
                                            <>
                                                {venue.contact_email}<br />
                                                {venue.contact_phone}<br />
                                                {venue.contact_website && (
                                                    <a href={venue.contact_website} target="_blank" rel="noopener noreferrer">
                                                        Website
                                                    </a>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="amenities-cell">
                                        {editingVenueId === venue.id ? (
                                            <div className="amenities-checkboxes">
                                                {amenitiesList.map(amenity => (
                                                    <div key={amenity.id}>
                                                        <input
                                                            type="checkbox"
                                                            id={`${venue.id}-${amenity.id}`}
                                                            checked={!!editedVenues[venue.id]?.[amenity.id]}
                                                            onChange={(e) => handleAmenityChange(venue.id, amenity.id, e.target.checked)}
                                                        />
                                                        <label htmlFor={`${venue.id}-${amenity.id}`}>{amenity.label}</label>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            formatAmenities(venue)
                                        )}
                                    </td>
                                    <td>
                                        {venue.courts?.length || 0} courts
                                        {venue.courts?.length > 0 && (
                                            <div className="court-details">
                                                {venue.courts.map(court => (
                                                    <div key={`${venue.id}-${court.court_number}`}>
                                                        Court {court.court_number}: {court.surface_type}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {venue.images?.length || 0} images
                                        {venue.images?.length > 0 && (
                                            <div className="image-preview">
                                                {venue.images.find(img => img.is_thumbnail) ? (
                                                    <img
                                                        src={venue.images.find(img => img.is_thumbnail).url}
                                                        alt="Thumbnail"
                                                        className="thumbnail-image"
                                                    />
                                                ) : (
                                                    <img
                                                        src={venue.images[0].url}
                                                        alt="Venue"
                                                        className="thumbnail-image"
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {new Date(venue.created_at).toLocaleDateString()}
                                    </td>
                                    <td>
                                        {editingVenueId === venue.id ? (
                                            <>
                                                <button
                                                    onClick={() => handleSaveClick(venue.id)}
                                                    className="save-button"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => setEditingVenueId(null)}
                                                    className="cancel-button"
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => handleEdit(venue.id)}
                                                    className="edit-button"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(venue.id)}
                                                    className="delete-button"
                                                >
                                                    Delete
                                                </button>
                                                <button
                                                    onClick={() => handleUploadClick(venue.id)}
                                                    className="upload-image-button"
                                                >
                                                    Add Image
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="15" className="no-venues">
                                    No venues to show
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default CrudView;